import {
  ChannelUnsubscribe,
  channelClient,
  ContractShapeOf,
  RequestPolicies,
  ContractEvent,
} from 'shared/channels';
import type {
  SharedDocumentSignedUrlRequest,
  SharedResourceSignedRequestResult,
} from 'shared/download/sharedResourceSignedRequest';
import type { DocumentCreateModel } from './documentCreateModel';
import type { DocumentModel, DocumentParentQuery } from './documentModel';
import { AttachmentCountsOfIssues, documentContract, DocumentUrlsRequest } from './documentContract';
import type { DocumentUrlsResult } from './documentUrlsModel';
import type { DrawingModel } from './drawingModel';
import type { DocumentEditData } from './editDocument';

/** Documents (attachments) and their drawings as served by the service worker. */
export const documentClient = {
  getOfParent(parent: DocumentParentQuery): Promise<DocumentModel[]> {
    return channelClient.request(documentContract, 'getOfParent', parent, RequestPolicies.read);
  },

  /** The PDF-eligible attachments of each issue (its own, not its messages'), e.g. for a report of many issues. */
  countAttachmentsOfIssues(issueIds: string[]): Promise<AttachmentCountsOfIssues> {
    return channelClient.request(documentContract, 'countAttachmentsOfIssues', { issueIds }, RequestPolicies.bulkRead);
  },

  /**
   * Resolves with the local id once the document is saved locally (the upload
   * follows). `uniqueId` comes back with the `createdEntity` event, so the
   * upload progress can follow the file. Long-running: many files queue as many
   * saves in the worker.
   */
  create(baseUrl: string, document: DocumentCreateModel, uniqueId: string): Promise<number> {
    return channelClient.request(
      documentContract,
      'create',
      { baseUrl, document, uniqueId },
      RequestPolicies.longRunning
    );
  },

  /** Resolves once the change is saved locally (the upload follows). */
  edit(change: DocumentEditData): Promise<void> {
    return channelClient.request(documentContract, 'edit', change, RequestPolicies.mutation);
  },

  /** Resolves once the document is removed locally (the server delete follows). */
  remove(localId: number): Promise<void> {
    return channelClient.request(documentContract, 'remove', { localId }, RequestPolicies.mutation);
  },

  /** Resolves once the drawing is saved locally (the upload follows). */
  createDrawing(drawing: DrawingModel, localId: number): Promise<void> {
    return channelClient.request(
      documentContract,
      'createDrawing',
      { drawing, localId },
      RequestPolicies.mutation
    );
  },

  getUrls(request: DocumentUrlsRequest): Promise<DocumentUrlsResult> {
    return channelClient.request(documentContract, 'getUrls', request, RequestPolicies.read);
  },

  /** The signed URL of a public issue link, or the status the server refused it with. */
  getSignedUrlSecuredByToken(
    request: SharedDocumentSignedUrlRequest
  ): Promise<SharedResourceSignedRequestResult> {
    return channelClient.request(
      documentContract,
      'getSignedUrlSecuredByToken',
      request,
      RequestPolicies.read
    );
  },

  /** Every document event; `switch (event.type)` narrows `event.data`. */
  onEvent(handler: (event: ContractEvent<ContractShapeOf<typeof documentContract>>) => void): ChannelUnsubscribe {
    return channelClient.onEvent(documentContract, handler);
  },
};
